<template>
  <div
    v-if="webinars.length && options.live_events_active"
    id="webinars"
    class="aq-base"
    :class="{ 'aq-base--open': liveEventsOpen }"
  >
    <button
      v-show="!phone"
      :aria-label="
        liveEventsOpen ? 'Close Live events panel' : 'Open live events panel'
      "
      aria-controls="webinars"
      :aria-expanded="liveEventsOpen"
      class="aq-button aq-widget__trigger"
      @click="toggleLiveEvents"
      @focus="handleFocus($event)"
    >
      <span :class="{ '-ml-10': browser === 'IE' }">Live Events</span>
    </button>

    <Transition name="fade">
      <div v-show="liveEventsOpen" class="aq-page">
        <div class="aq-page__head">
          <figure class="aq-page__head-picture">
            <img :src="image.src" alt="image.alt" />
          </figure>
          <div class="aq-page__head-content">
            <h2 class="aq-title">Find a live event...</h2>
            <label for="aq__search" />
            <input
              id="aq__search"
              v-model="search"
              aria-label="Search for a webinar"
              class="aq-input aq-input--search"
              name="search"
              type="search"
              size="40"
              placeholder="Tags, titles, dates ..."
              aria-controls="webinars_list"
            />
            <span
              class="aq-icon__search aq-pointer"
              aria-label="Search for a webinar"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="27.012"
                height="27.012"
                viewBox="0 0 27.012 27.012"
              >
                <path
                  d="M26.826,25.407,20.021,18.6a.626.626,0,0,0-.448-.185h-.543a10.863,10.863,0,1,0-.612.617v.538a.649.649,0,0,0,.185.448l6.806,6.806a.634.634,0,0,0,.9,0l.522-.522A.634.634,0,0,0,26.826,25.407ZM10.973,20.258a9.285,9.285,0,1,1,9.285-9.285A9.278,9.278,0,0,1,10.973,20.258Z"
                />
              </svg>
            </span>
          </div>
        </div>

        <div class="aq-page__navigation">
          <ul class="aq-menu" @click="showEventInfo = false">
            <li class="aq-menu__item">
              <button
                :class="{ 'aq-router-link-exact-active': !filter }"
                @click="handleFilter('')"
              >
                All
              </button>
            </li>
            <li class="aq-menu__item">
              <button
                :class="{
                  'aq-router-link-exact-active': filter === 'scheduled'
                }"
                @click="handleFilter('scheduled')"
              >
                Scheduled
              </button>
            </li>
            <li class="aq-menu__item">
              <button
                :class="{
                  'aq-router-link-exact-active': filter === 'ondemand'
                }"
                @click="handleFilter('ondemand')"
              >
                On Demand
              </button>
            </li>
          </ul>
        </div>

        <div
          v-if="list.length"
          id="webinars_list"
          aria-live="polite"
          class="aq-page__body aq-page__body"
        >
          <p role="status" aria-live="polite" aria-atomic="true">
            {{ list.length }} items found.
          </p>

          <div
            v-for="webinar in list"
            :id="webinar.slug"
            :key="webinar.slug"
            :ref="webinar.slug"
            class="aq-entry aq-entry--summary"
          >
            <button
              class="aq-button aq-button--slide"
              :aria-label="`View information for ${webinar.title}`"
              :data-ref="webinar.slug"
              @click="expandEntry"
              @focus="handleFocus($event)"
            />
            <div class="aq-entry__body aq-entry__body--summary">
              <h2 class="aq-body__title">
                {{ htmlDecode(webinar.title) }}
              </h2>
              <div class="aq-body__content">
                <p v-if="webinar.summary" class="aq-body__content-description">
                  {{ htmlDecode(webinar.summary) }}
                </p>
                <a
                  :href="webinar.link"
                  target="_blank"
                  class="aq-button aq-button--more"
                  @focus="handleFocus($event)"
                >
                  {{ htmlDecode(webinar.webinar_button_copy) }}
                </a>
              </div>
            </div>
            <p v-if="!webinar.scheduled" class="aq-entry__date">
              <code class="aq-date">
                <strong>On Demand</strong>
              </code>
            </p>
            <p v-else class="aq-entry__date">
              <code class="aq-date">
                <strong>{{ webinar.date }}</strong>
              </code>
              <code class="aq-date">
                {{ htmlDecode(webinar.time) }}
              </code>
            </p>
          </div>
        </div>
        <div v-else class="aq-page__body aq-page__body--solo">
          <p class="aq-text">
            No items found!
          </p>
        </div>
        <button
          v-show="liveEventsOpen"
          aria-label="Close live events panel"
          class="aq-button aq-widget__close"
          @click="toggleLiveEvents"
          @focus="handleFocus($event)"
        />
      </div>
    </Transition>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

const matches = q => term => term.toLowerCase().includes(q)

export default {
  name: 'Webinars',

  data: () => ({
    search: '',
    filter: '',
    expandedEntries: []
    // liveEventsOpen: false
  }),

  computed: {
    ...mapState(['liveEventsOpen', 'phone', 'browser']),
    ...mapGetters(['webinars', 'options']),

    list() {
      const filter = this.filter
      const match = matches(this.search.toLowerCase())
      const webinars = this.webinars

      return webinars
        .filter(
          ({ title, date, tags }) =>
            match(title.toLowerCase()) ||
            match(date) ||
            match(tags.toLowerCase())
        )
        .filter(item => {
          switch (filter) {
            case 'ondemand':
              return !item.scheduled
            case 'scheduled':
              return item.scheduled
            default:
              return item
          }
        })
    },

    image() {
      return {
        src: this.options?.live_events_image?.sizes?.large,
        alt: this.options?.live_events_image?.alt
      }
    }
  },

  methods: {
    handleFilter(filter) {
      this.filter = filter
    },

    toggleLiveEvents() {
      this.$store.commit('toggleLiveEventsState')
    },

    expandEntry(e) {
      e.stopPropagation()
      let dataRef = e.target.dataset.ref
      const target = document.getElementById(dataRef)
      target.classList.toggle('aq-entry--expanded')
    },

    handleFocus() {
      /*
       * Traps focus within the menu. Make sure to add the
       * focus handle on the first and last focusable elements
       */

      const domNode = document.getElementById('webinars')
      const focusableEls = [...domNode.querySelectorAll('button, a')]
      const firstFocusableEl = focusableEls[0]
      const lastFocusableEl = focusableEls[focusableEls.length - 1]

      console.log(lastFocusableEl)
      event.target.addEventListener('keydown', e => {
        const isTabPressed = e.code === 'Tab'

        if (!isTabPressed) {
          return
        }

        if (e.shiftKey) {
          if (
            document.activeElement === firstFocusableEl &&
            this.liveEventsOpen
          ) {
            lastFocusableEl.focus()
            e.preventDefault()
          }
        } else {
          if (document.activeElement === lastFocusableEl) {
            firstFocusableEl.focus()
            e.preventDefault()
          }
        }
      })
    }
  }
}
</script>
